import { ColumnAccessor, ColumnAccessorSortable } from '@/types/columns';
import { DisplayMode } from '@/types/display-mode';
import { KPIComparison } from '@/types/kpi-comparison';
import { Store } from '@/types/store';

export interface BusinessColumnVisibilityParams {
  kpiComparison: KPIComparison;
  hasCouponsInCurrentView: boolean;
  hasAnyShopifyVariantId: boolean;
  hasDiscountedPrice: boolean;
  store: Store;
  isInternalUser: boolean;
  displayMode: DisplayMode;
}

/**
 * Returns visibility for business-driven columns (sortable / configurable).
 * Single source of truth for table columnVisibility and column-order modal.
 */
export function getBusinessColumnVisibility(
  params: BusinessColumnVisibilityParams
): Partial<Record<ColumnAccessor, boolean>> {
  const {
    kpiComparison,
    hasCouponsInCurrentView,
    hasAnyShopifyVariantId,
    hasDiscountedPrice,
    store,
    isInternalUser,
    displayMode,
  } = params;

  const none = kpiComparison === KPIComparison.NONE;
  const simplified = kpiComparison === KPIComparison.SIMPLIFIED;
  const discountModes =
    kpiComparison === KPIComparison.NONE ||
    kpiComparison === KPIComparison.SIMPLIFIED ||
    kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
    kpiComparison === KPIComparison.DISCOUNT_LOWER;

  return {
    couponExpirationDate: hasCouponsInCurrentView,
    couponTerms: hasCouponsInCurrentView,
    couponHowToUse: hasCouponsInCurrentView,
    buyingPrice: none,
    chooseDiscount: discountModes,
    choosePrice: discountModes,
    description: none,
    discountedPrice:
      (none || simplified) && (isInternalUser || hasDiscountedPrice),
    gtin: none,
    listPrice: discountModes,
    madeIn: none,
    marginPercentage: isInternalUser ? none || simplified : false,
    potentialSalesVolume: none || simplified,
    shippingPrice: none || simplified,
    sku: none,
    shopifyVariantId: hasAnyShopifyVariantId,
    stockQuantity: none || kpiComparison === KPIComparison.STOCK,
    productCategory: none || simplified,
    sourceUrl: none,
    productType: none,
    validationErrors: displayMode !== DisplayMode.DESELECTED,
    vatRate: store === Store.FR && none,
    eeePrice: store === Store.FR && none,
  };
}

/**
 * Returns whether a sortable column is visible given current business params.
 * Use in column-order modal to filter which columns to show.
 * Columns not in the business visibility map (e.g. isBestSeller) are considered visible.
 */
export function isSortableColumnVisible(
  columnAccessor: ColumnAccessorSortable,
  params: BusinessColumnVisibilityParams
): boolean {
  const visibility = getBusinessColumnVisibility(params);
  const value = visibility[columnAccessor];
  return value === undefined ? true : value;
}
